<template>
    <modal
        v-if="show"
        class="ViewVoiceIntegrity"
        :title="title"
        :form="form"
        :submitText="submitTextModal"
        :cancelText="'Cancel'"
        :backText="'Previous'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy,
        }"
        :actionLeft="actionLeft"
        :submitDisabled="submitDisabled"
        :hideBack="!showBackButton"
        :hideCancel="!showCancelButton"
        @back="handlePreviousStep"
        @closed="handleClosed"
        @submit="handleNextStep"
    >
        <div v-if="loading" class="loading">
            <basic-loader aria-label="VoiceIntegrityModal" />
        </div>
        <template v-else>
            <div
                v-if="currentStep === 1"
                class="ViewVoiceIntegrity__content ViewVoiceIntegrity__preview"
            >
                <div class="ViewVoiceIntegrity__previewTitle">Voice Integrity</div>

                <div class="ViewVoiceIntegrity__previewDescription">
                    Remediate spam labels on your phone numbers with Voice Integrity.
                </div>

                <div class="ViewVoiceIntegrity__previewImage">
                    <img
                        :src="`${$window.Config.cdn_url}/images/voice-integrity.png`"
                        alt="Voice integrity"
                    />
                </div>
            </div>

            <div v-if="currentStep === 2" class="ViewVoiceIntegrity__content">
                <div class="ViewVoiceIntegrity__row">
                    <form-dropdown
                        id="dropdown-voice_integrity"
                        ref="select"
                        :value="form.use_case"
                        label="Use Case"
                        placeholder="Use Case"
                        :error="form.errors.get('use_case')"
                        :settings="settingUseCases"
                        @input="updateUseCase"
                    />
                </div>
                <div class="columns">
                    <div class="column is-6" style="align-items: flex-start">
                        <form-input
                            v-model="form.business_employee_count"
                            label="Company size"
                            placeholder="Company size"
                            tooltip="Total number of employees in your business."
                            type="number"
                            :error="form.errors.get('business_employee_count')"
                            :min="0"
                        />
                    </div>
                    <div class="column is-6" style="align-items: flex-start">
                        <form-input
                            v-model="form.average_business_day_call_volume"
                            tooltip="Estimated daily call volume for your business."
                            label="Average calls per day"
                            placeholder="Average calls per day"
                            type="number"
                            :error="form.errors.get('average_business_day_call_volume')"
                            :min="0"
                        />
                    </div>
                </div>
                <div>
                    <form-textarea
                        v-model="form.notes"
                        placeholder="Notes"
                        tooltip="Extra details on your use case."
                        label="Notes"
                    />
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import filters from '../../../../../../../vue/filters';

export default {
    emits: ['closed'],

    data() {
        return {
            form: new Form({
                use_case: null,
                business_employee_count: null,
                average_business_day_call_volume: null,
                notes: null,
            }),
            currentStep: 1,
            steps: 2,
            showBackButton: false,
            showCancelButton: true,
            actionLeft: true,
            errorMessage: '',
            loading: true,
        };
    },

    computed: {
        voiceIntegrityForm() {
            return this.voiceIntegrity?.additionalInfo;
        },

        show() {
            return this.showVoiceIntegrityModal;
        },

        price() {
            if (this.complianceBillingInfo.interval === 'yearly') {
                return `${filters.currency(this.complianceBillingInfo.price, 2)}/year`;
            }

            return `${filters.currency(this.complianceBillingInfo.price, 2)}/mo`;
        },

        submitTextModal() {
            if (this.voiceIntegrityForm) {
                return 'Save';
            }
            return this.currentStep === this.steps ? 'Create' : 'Next';
        },

        submitDisabled() {
            return false;
        },

        title() {
            if (this.voiceIntegrityForm) {
                return 'Edit a Voice Integrity';
            }

            return 'Create a Voice Integrity';
        },

        settingUseCases() {
            return {
                maxItems: 1,
                options: this.options,
                valueField: 'id',
                dropdownParent: 'body',
                render: {
                    item: this.dropdownItem,
                    option: this.dropdownOption,
                },
            };
        },

        options() {
            const useCases = `
                Abandoned Cart
                Appointment Reminders
                Appointment Scheduling
                Asset Management
                Automated Support
                Call Tracking
                Click to Call
                Contact Tracing
                Contactless Delivery
                Customer Support
                Dating/Social
                Delivery Notifications
                Distance Learning
                Emergency Notifications
                Employee Notifications
                Exam Proctoring
                Field Notifications
                First Responder
                Fraud Alerts
                Group Messaging
                Identify & Verification
                Intelligent Routing
                Lead Alerts
                Lead Distribution
                Lead Generation
                Lead Management
                Lead Nurturing
                Marketing Events
                Mass Alerts
                Meetings/Collaboration
                Order Notifications
                Outbound Dialer
                Phone System
                Pharmacy
                Purchase Confirmation
                Remote Appointments
                Rewards Program
                Self-Service
                Service Alerts
                Shift Management
                Survey/Research
                Telehealth
                Telemarketing
                Therapy (Individual+Group)
                Tutoring
            `
                .split('\n')
                .filter((i) => !!i.trim())
                .map((item) => ({ label: item.trim(), value: item.trim(), id: item.trim() }));

            return useCases;
        },

        ...mapState(stores.CallingStore, ['showVoiceIntegrityModal']),
        ...mapState(stores.ComplianceProfile, ['complianceBillingInfo', 'voiceIntegrity']),
    },

    watch: {
        show(value) {
            if (value) {
                this.init();
            } else {
                this.reset();
            }
        },

        'form.business_employee_count'() {
            this.form.errors.clear('business_employee_count');
        },

        'form.average_business_day_call_volume'() {
            this.form.errors.clear('average_business_day_call_volume');
        },

        'form.use_case'() {
            this.form.errors.clear('use_case');
        },
    },

    methods: {
        async init() {
            this.loading = true;
            await this.fetchVoiceIntegrity();
            this.calculateButtons();
            this.initStep();
            await this.$delay(200);
            await this.preFillForm();
            this.loading = false;
        },

        async fetchVoiceIntegrity() {
            try {
                await this.$store.dispatch('fetchVoiceIntegrity');
            } catch (e) {
                console.log(e);
            }
        },

        async reset() {
            this.form.reset();
            this.loading = true;
            this.actionLeft = true;
            this.errorMessage = '';
        },

        async preFillForm() {
            this.form.use_case = this.voiceIntegrityForm.use_case;

            await this.$delay(200);

            this.form.business_employee_count = this.voiceIntegrityForm.business_employee_count;
            this.form.average_business_day_call_volume =
                this.voiceIntegrityForm.average_business_day_call_volume;
            this.form.notes = this.voiceIntegrityForm.notes;
        },

        setStep(step) {
            this.currentStep = step;
            this.calculateButtons();
        },

        calculateButtons() {
            if (this.voiceIntegrityForm) {
                this.showBackButton = false;
                this.showCancelButton = true;

                return;
            }

            if (this.currentStep > 1) {
                this.showBackButton = true;
                this.showCancelButton = false;
            } else {
                this.showBackButton = false;
                this.showCancelButton = true;
            }
        },

        initStep() {
            if (this.voiceIntegrityForm) {
                this.currentStep = 2;
                this.showBackButton = false;
                this.showCancelButton = true;

                return;
            }

            this.currentStep = 1;
            this.showBackButton = true;
            this.showCancelButton = false;
        },

        async handleNextStep() {
            if (this.currentStep === 1) {
                this.setStep(2);

                return;
            }

            if (this.currentStep === 2) {
                await this.handleSubmit();
            }
        },

        handlePreviousStep() {
            if (this.currentStep === 2) {
                this.setStep(1);
            }

            if (this.currentStep === 3) {
                this.setStep(2);
            }
        },

        updateUseCase(useCase) {
            this.form.use_case = useCase;
        },

        async handleSubmit() {
            try {
                this.form.busy = true;

                await this.$store.dispatch('setVoiceIntegrity', this.form);

                this.handleClosed();

                this.$store.dispatch('showToast', {
                    type: 'success',
                    message: 'Voice Integrity created!',
                });
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSubmitVoiceIntegrity');
                if (e.response.data) {
                    this.form.errors.record(e.response.data);
                }
            } finally {
                this.form.busy = false;
            }
        },

        handleClosed() {
            this.$store.dispatch('handleShowVoiceIntegrityModal', false);
            this.$emit('closed');
        },

        dropdownItem(item, escape) {
            return `<div class="days-select-item">
                    ${escape(item.label)}
                </div>`;
        },

        dropdownOption(item, escape) {
            return `<div class="days-select-item">
                    ${escape(item.label)}
                </div>`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'styles.module';
</style>
